import { useCallback, useEffect, useRef, useState } from 'react';

export type ToastKind = 'success' | 'error' | 'warn';

export interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
}

// WCAG 2.2.2 (Pause, Stop, Hide): give readers time to read and act before
// auto-dismiss. Success clears a 5s floor; error/warn linger longer. Every toast
// also stays manually dismissible via the × button, which is the "hide" mechanism.
const DEFAULT_DURATION_MS: Record<ToastKind, number> = {
  success: 5000,
  warn: 8000,
  error: 8000,
};

// Hard floor: even an explicit durationMs override cannot drop a toast below the
// 5s WCAG-readability floor. (It can still lengthen — e.g. a warn that only needs
// 5s rather than the 8s default — but never shorten past 5s.)
const MIN_DURATION_MS = 5000;

// Module-level so ids stay unique even across multiple mounted regions.
let toastSeq = 0;

export interface UseToasts {
  toasts: ToastItem[];
  pushToast: (message: string, kind?: ToastKind, durationMs?: number) => number;
  dismiss: (id: number) => void;
}

/**
 * Toast/snackbar state with auto-dismiss + manual dismiss. Owns the id counter
 * and per-toast timer lifecycle, clearing pending timers on unmount so there is
 * no stray setState after the region is gone. Pair with <ToastRegion />.
 *
 * Extracted from five hand-rolled copies (see
 * docs/solutions/developer-experience/toast-region-duplicated-across-panes-extract-shared-2026-07-02.md).
 */
export function useToasts(): UseToasts {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback(
    (message: string, kind: ToastKind = 'success', durationMs?: number): number => {
      const id = ++toastSeq;
      setToasts((prev) => [...prev, { id, message, kind }]);
      const ms = Math.max(durationMs ?? DEFAULT_DURATION_MS[kind], MIN_DURATION_MS);
      const handle = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        timers.current.delete(id);
      }, ms);
      timers.current.set(id, handle);
      return id;
    },
    [],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach(clearTimeout);
      map.clear();
    };
  }, []);

  return { toasts, pushToast, dismiss };
}
